import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { BugIcon, LightbulbIcon, QuestionIcon, RowsIcon, KanbanIcon, TrashIcon, EyeIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

const CATEGORY_META = {
  Bug:        { icon: BugIcon,       cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  Suggestion: { icon: LightbulbIcon, cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  Question:   { icon: QuestionIcon,  cls: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
};
const STATUS_META = {
  "New":         { cls: "bg-blue-500/10 text-blue-400 border-blue-500/30",       col: "border-blue-500/40" },
  "In Progress": { cls: "bg-amber-500/10 text-amber-400 border-amber-500/30",    col: "border-amber-500/40" },
  "Resolved":    { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", col: "border-emerald-500/40" },
  "Rejected":    { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",      col: "border-zinc-500/40" },
};
const STATUS_ORDER = ["New", "In Progress", "Resolved", "Rejected"];

export default function Feedback() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Administrator" || user?.role === "Management";
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("mes_feedback_view") || "grid");
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { localStorage.setItem("mes_feedback_view", view); }, [view]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback/");
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load feedback");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (catFilter !== "all" && r.category !== catFilter) return false;
      if (q && !(
        (r.subject || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q) ||
        (r.created_by || "").toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [rows, statusFilter, catFilter, search]);

  const board = useMemo(() => {
    const b = { "New": [], "In Progress": [], "Resolved": [], "Rejected": [] };
    filtered.forEach(r => { const s = STATUS_ORDER.includes(r.status) ? r.status : "New"; b[s].push(r); });
    return b;
  }, [filtered]);

  const updateStatus = async (id, status, notes) => {
    try {
      await api.patch(`/feedback/${id}/status`, { status, notes });
      toast.success(`Marked ${status}`);
      load();
      setViewing(v => v && v.id === id ? { ...v, status, notes: notes ?? v.notes } : v);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this feedback permanently?")) return;
    try {
      await api.delete(`/feedback/${id}`);
      toast.success("Deleted");
      setViewing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Voice of the User</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">User Feedback</h1>
          <p className="text-xs text-zinc-500 mt-1">
            {isAdmin ? "Triage bugs, suggestions and questions raised by your team." : "Your submitted feedback — track its status here."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
            <button
              data-testid="feedback-view-grid"
              onClick={() => setView("grid")}
              className={`px-3 h-9 text-xs font-medium uppercase tracking-widest flex items-center gap-1.5 ${view === "grid" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
            ><RowsIcon size={13} weight="bold" />Grid</button>
            <button
              data-testid="feedback-view-kanban"
              onClick={() => setView("kanban")}
              className={`px-3 h-9 text-xs font-medium uppercase tracking-widest flex items-center gap-1.5 border-l border-[var(--border)] ${view === "kanban" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
            ><KanbanIcon size={13} weight="bold" />Kanban</button>
          </div>
          <button
            data-testid="feedback-refresh"
            onClick={load}
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm flex items-center gap-1.5 text-zinc-300"
            title="Refresh"
          ><ArrowsClockwiseIcon size={13} weight="bold" />Refresh</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          data-testid="feedback-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search subject, description, submitter…"
          className="w-64 h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
          data-nouppercase
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="feedback-status-filter" className="bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="feedback-cat-filter" className="bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm w-40"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
            <SelectItem value="all">All Categories</SelectItem>
            {Object.keys(CATEGORY_META).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-zinc-500 ml-auto font-mono" data-testid="feedback-count">{filtered.length} of {rows.length}</div>
      </div>

      {loading && <div className="text-center py-10 text-zinc-500 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="tactical-card p-10 text-center">
          <div className="text-zinc-500 text-sm">No feedback yet. Click <span className="text-[var(--accent)] font-semibold">Feedback</span> in the header to raise the first one.</div>
        </div>
      )}

      {!loading && rows.length > 0 && view === "grid" && (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-4 py-3 font-medium">Category</th>
                <th className="text-left px-4 py-3 font-medium">Subject</th>
                <th className="text-left px-4 py-3 font-medium">Submitted By</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const meta = CATEGORY_META[r.category] || CATEGORY_META.Suggestion;
                const smeta = STATUS_META[r.status] || STATUS_META.New;
                const Icon = meta.icon;
                return (
                  <tr key={r.id} data-testid={`feedback-row-${r.id}`} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${meta.cls}`}>
                        <Icon size={11} weight="bold" />{r.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-white font-medium truncate" title={r.subject}>{r.subject}</div>
                      {r.description && <div className="text-xs text-zinc-500 truncate mt-0.5" title={r.description}>{r.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400 font-mono">{r.created_by_name || r.created_by || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${smeta.cls}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`feedback-view-${r.id}`}
                          onClick={() => setViewing(r)}
                          className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-zinc-400 hover:text-white"
                          title="View & manage"
                        ><EyeIcon size={14} /></button>
                        {isAdmin && (
                          <button
                            data-testid={`feedback-delete-${r.id}`}
                            onClick={() => remove(r.id)}
                            className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"
                            title="Delete"
                          ><TrashIcon size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-zinc-500 text-sm">No matches for the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length > 0 && view === "kanban" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="feedback-kanban">
          {STATUS_ORDER.map(s => {
            const smeta = STATUS_META[s];
            const items = board[s];
            return (
              <div key={s} data-testid={`feedback-column-${s}`} className={`tactical-card p-3 border-t-2 ${smeta.col}`}>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="font-display font-bold text-sm text-white">{s}</div>
                  <span className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${smeta.cls}`}>{items.length}</span>
                </div>
                <div className="space-y-2 max-h-[65vh] overflow-y-auto">
                  {items.map(r => {
                    const meta = CATEGORY_META[r.category] || CATEGORY_META.Suggestion;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={r.id}
                        onClick={() => setViewing(r)}
                        data-testid={`feedback-card-${r.id}`}
                        className="w-full text-left p-2.5 rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/50 hover:bg-[var(--bg-2)]/60 hover:border-[var(--accent)]/40 transition"
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest border ${meta.cls}`}>
                            <Icon size={9} weight="bold" />{r.category}
                          </span>
                          <span className="text-[10px] text-zinc-500 font-mono ml-auto">{(r.created_at || "").slice(5, 10)}</span>
                        </div>
                        <div className="text-sm font-medium text-white line-clamp-2">{r.subject}</div>
                        <div className="text-[10px] text-zinc-500 mt-1 font-mono truncate">{r.created_by_name || r.created_by}</div>
                      </button>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="text-[11px] text-zinc-600 italic text-center py-4">No items</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View / Manage dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="feedback-detail-dialog" description="Full detail view of the feedback ticket with attachments and status thread.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <span>Feedback Detail</span>
              {viewing && (() => {
                const meta = CATEGORY_META[viewing.category] || CATEGORY_META.Suggestion;
                const Icon = meta.icon;
                return (
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${meta.cls}`}>
                    <Icon size={11} weight="bold" />{viewing.category}
                  </span>
                );
              })()}
              {viewing && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${(STATUS_META[viewing.status] || STATUS_META.New).cls}`}>{viewing.status}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><div className="overline mb-0.5">Submitted By</div><div className="font-mono">{viewing.created_by_name || viewing.created_by || "—"}</div></div>
                <div><div className="overline mb-0.5">Submitted At</div><div className="font-mono">{(viewing.created_at || "").slice(0, 19).replace("T", " ")}</div></div>
                {viewing.resolved_by && <div><div className="overline mb-0.5">Resolved By</div><div className="font-mono">{viewing.resolved_by}</div></div>}
                {viewing.resolved_at && <div><div className="overline mb-0.5">Resolved At</div><div className="font-mono">{(viewing.resolved_at || "").slice(0, 19).replace("T", " ")}</div></div>}
              </div>
              <div>
                <div className="overline mb-1">Subject</div>
                <div className="font-semibold text-white">{viewing.subject}</div>
              </div>
              {viewing.description && (
                <div>
                  <div className="overline mb-1">Description</div>
                  <div className="text-sm text-zinc-300 whitespace-pre-wrap bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 py-2">{viewing.description}</div>
                </div>
              )}
              {viewing.screenshot && (
                <div>
                  <div className="overline mb-1">Screenshot</div>
                  <img src={viewing.screenshot} alt="screenshot" className="max-w-full max-h-96 border border-[var(--border)] rounded-sm" data-testid="feedback-detail-screenshot" />
                </div>
              )}
              {isAdmin && (
                <>
                  <div>
                    <div className="overline mb-1">Admin Notes</div>
                    <textarea
                      data-testid="feedback-notes"
                      value={viewing.notes || ""}
                      onChange={e => setViewing({ ...viewing, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] resize-y"
                      placeholder="Triage notes visible only to admins"
                      data-nouppercase
                    />
                  </div>
                  <div>
                    <div className="overline mb-1.5">Update Status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_ORDER.map(s => (
                        <button
                          key={s}
                          data-testid={`feedback-set-${s.replace(/\s+/g, "-")}`}
                          onClick={() => updateStatus(viewing.id, s, viewing.notes)}
                          className={`px-3 h-9 text-xs font-semibold uppercase tracking-widest border rounded-sm ${viewing.status === s ? (STATUS_META[s].cls + " ring-1 ring-current") : "border-[var(--border)] text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]"}`}
                        >{s}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {!isAdmin && viewing.notes && (
                <div>
                  <div className="overline mb-1">Admin Notes</div>
                  <div className="text-sm text-zinc-300 whitespace-pre-wrap bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 py-2">{viewing.notes}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {isAdmin && viewing && (
              <button
                data-testid="feedback-detail-delete"
                onClick={() => remove(viewing.id)}
                className="px-4 py-2 rounded-sm border border-red-500/30 text-red-400 text-sm font-semibold hover:bg-red-500/10"
              >Delete</button>
            )}
            <button
              onClick={() => setViewing(null)}
              className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white"
            >Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
